import type { CallAnalysisAgentPerformance } from '@marnic/shared';
import { MiniSection } from '../primitives/MiniSection';
import { ScoreBar } from '../primitives/ScoreBar';
import { RiskBadge } from '../primitives/RiskBadge';
import { EvidenceBadge } from '../EvidenceBadge';

export function AgentPerformanceCard({ performance }: { performance: CallAnalysisAgentPerformance | null }): JSX.Element | null {
  if (!performance) return null;
  const sub = performance.subScores;
  const useV2 = !!sub;

  return (
    <MiniSection
      title="Agent coaching"
      action={
        performance.scoreConfidence && (
          <span className="text-[10px] text-ink-500 uppercase">
            confidence: {performance.scoreConfidence}
          </span>
        )
      }
    >
      {performance.notScorableReason && (
        <p className="rounded bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
          Not scorable: {performance.notScorableReason}
        </p>
      )}

      {useV2 ? (
        <div className="space-y-1">
          {typeof performance.overallScore === 'number' && (
            <ScoreBar label="Overall" score={performance.overallScore} confidence={performance.scoreConfidence} />
          )}
          <ScoreBar label="Discovery" score={sub!.discovery} confidence={performance.scoreConfidence} />
          <ScoreBar label="Qualification" score={sub!.qualification} confidence={performance.scoreConfidence} />
          <ScoreBar label="Product fit" score={sub!.productAndCustomisationFit} confidence={performance.scoreConfidence} />
          <ScoreBar label="Objections" score={sub!.objectionHandling} confidence={performance.scoreConfidence} />
          <ScoreBar label="Close" score={sub!.closeAndNextStep} confidence={performance.scoreConfidence} />
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-1 text-center text-[11px]">
          <span className="rounded bg-white px-1 py-1">Overall {performance.overallScore}/10</span>
          <span className="rounded bg-white px-1 py-1">Discovery {performance.discoveryScore}/10</span>
          <span className="rounded bg-white px-1 py-1">Objection {performance.objectionHandlingScore}/10</span>
          <span className="rounded bg-white px-1 py-1">Close {performance.closingScore}/10</span>
        </div>
      )}

      {(performance.coachingPriority || performance.singleCoachingPoint) && (
        <p className="rounded bg-sky-50 border border-sky-100 px-2 py-1 text-[11px] text-sky-900 leading-relaxed">
          <span className="font-semibold">Priority:</span>{' '}
          {performance.coachingPriority ?? performance.singleCoachingPoint}
        </p>
      )}

      {performance.nextCallPrep && (
        <p className="text-[11px] leading-relaxed">
          <span className="text-ink-500">Next call prep: </span>
          {performance.nextCallPrep}
        </p>
      )}

      {performance.strengths.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-ink-500 mb-0.5">Strengths</div>
          <ul className="space-y-0.5 text-[11px]">
            {performance.strengths.slice(0, 4).map((s, idx) => (
              <li key={idx} className="leading-relaxed">
                {s.text}
                {s.evidenceRef && <span className="ml-1"><EvidenceBadge id={s.evidenceRef} /></span>}
                {s.impact && <span className="text-ink-500 italic"> — {s.impact}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {performance.improvementAreas.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-ink-500 mb-0.5">Improve</div>
          <ul className="space-y-0.5 text-[11px]">
            {performance.improvementAreas.slice(0, 4).map((i, idx) => (
              <li key={idx} className="leading-relaxed flex flex-wrap items-start gap-1">
                <span>
                  <span className="font-medium">{i.area}</span>
                  {i.shouldHaveDone && <span> — {i.shouldHaveDone}</span>}
                  {i.exampleScript && <span className="block italic text-ink-500">e.g. {i.exampleScript}</span>}
                </span>
                {i.revenueRisk && <RiskBadge severity={i.revenueRisk} label={`risk: ${i.revenueRisk}`} />}
                {i.evidenceRef && <EvidenceBadge id={i.evidenceRef} />}
              </li>
            ))}
          </ul>
        </div>
      )}

      {performance.missedSignals && performance.missedSignals.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-ink-500 mb-0.5">Missed signals</div>
          <ul className="space-y-0.5 text-[11px]">
            {performance.missedSignals.slice(0, 4).map((m, idx) => (
              <li key={idx} className="leading-relaxed">
                <span className="font-medium">{m.signal}</span>
                {m.shouldHaveDone && <span> — should have: {m.shouldHaveDone}</span>}
                {m.evidenceRef && <span className="ml-1"><EvidenceBadge id={m.evidenceRef} /></span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </MiniSection>
  );
}
